/**
 * Battlefield rows and the hand.
 *
 * Rows render mini frames, the hand renders hand frames. Both are flex strips
 * that scroll horizontally, which is what makes the mobile layout in UI_UX
 * section 8 fall out of the same markup rather than needing a second tree.
 *
 * Every per-card interaction decision arrives as a prop from Battlefield, which
 * is the only place that reads legalActions. A row never decides a rule.
 */

import type { MouseEvent } from 'react';
import type { GameState } from '@/engine';
import { CardFrame, viewFromObject } from '@/ui/cards';
import { WalletTile } from './WalletTile';
import styles from './Battlefield.module.css';

export interface CardInteraction {
  playable?: boolean;
  unaffordable?: boolean;
  reason?: string;
  validTarget?: boolean;
  dimmed?: boolean;
  selected?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
}

export interface BoardRowProps {
  state: GameState;
  ids: string[];
  /** Characters render larger than Wallets. */
  variant: 'characters' | 'wallets';
  label: string;
  emptyText: string;
  interactionFor: (instanceId: string) => CardInteraction;
  /** Pointer handlers that drive the zoom panel. */
  zoomHandlers: ZoomHandlers;
}

/** What a row needs to open and close the zoom panel for one card. */
export interface ZoomHandlers {
  onEnter: (instanceId: string) => void;
  onLeave: () => void;
  onPin: (instanceId: string) => void;
}

export function BoardRow({ state, ids, variant, label, emptyText, interactionFor, zoomHandlers }: BoardRowProps) {
  const rowClass = [
    styles.row,
    variant === 'characters' ? styles.characterRow : styles.walletRow,
    ids.length === 0 ? styles.rowEmpty : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (ids.length === 0) {
    return (
      <div className={rowClass} aria-label={label}>
        {emptyText}
      </div>
    );
  }

  return (
    <div className={rowClass} aria-label={label} data-row={variant}>
      {ids.map((id) => {
        const interaction = interactionFor(id);
        const pointer = {
          onPointerEnter: () => zoomHandlers.onEnter(id),
          onPointerLeave: zoomHandlers.onLeave,
          onContextMenu:
            interaction.onContextMenu ??
            ((e: MouseEvent<HTMLDivElement>) => {
              e.preventDefault();
              zoomHandlers.onPin(id);
            }),
        };

        if (variant === 'wallets') {
          return <WalletTile key={id} state={state} instanceId={id} {...interaction} {...pointer} />;
        }

        const view = viewFromObject(state, id);
        if (!view) return null;
        return (
          <CardFrame
            key={id}
            card={view}
            size="mini"
            {...interaction}
            {...pointer}
            data-testid={`board-card-${id}`}
          />
        );
      })}
    </div>
  );
}

export interface HandProps {
  state: GameState;
  playerId: string;
  interactionFor: (instanceId: string) => CardInteraction;
  zoomHandlers: ZoomHandlers;
}

export function Hand({ state, playerId, interactionFor, zoomHandlers }: HandProps) {
  const p = state.players[playerId];
  const ids = p?.hand ?? [];

  if (ids.length === 0) {
    return (
      <div className={`${styles.hand} ${styles.handEmpty}`} aria-label="Your hand">
        Hand empty
      </div>
    );
  }

  return (
    <div className={styles.hand} aria-label="Your hand" data-testid="hand">
      {ids.map((id) => {
        const view = viewFromObject(state, id);
        if (!view) return null;
        const interaction = interactionFor(id);
        return (
          <CardFrame
            key={id}
            card={view}
            size="hand"
            {...interaction}
            onContextMenu={(e) => {
              e.preventDefault();
              zoomHandlers.onPin(id);
            }}
            onPointerEnter={() => zoomHandlers.onEnter(id)}
            onPointerLeave={zoomHandlers.onLeave}
            data-testid={`hand-card-${id}`}
          >
            {/*
              The affordability reason lives on the card, not in a toast. A
              player looking at a dim card wants to know why it is dim, and the
              answer belongs where they are already looking.
            */}
            {interaction.unaffordable && interaction.reason ? (
              <span className={styles.affordReason} data-testid={`reason-${id}`}>
                {interaction.reason}
              </span>
            ) : null}
          </CardFrame>
        );
      })}
    </div>
  );
}

/**
 * The opponent hand as a fan of overlapping card backs sized to the bar it
 * sits in. The count is public, the contents are not, so these are decoration
 * with an accessible label rather than real frames.
 */
export function OpponentHand({ count }: { count: number }) {
  return (
    <span className={styles.opponentHand} aria-label={`Opponent hand, ${count} cards`} title={`${count} cards in hand`}>
      {Array.from({ length: Math.min(count, 8) }, (_, i) => (
        <span key={i} className={styles.opponentHandCard} aria-hidden="true" />
      ))}
    </span>
  );
}
